/**
 * Environmental labelling of packaging — art. 219 c.5 D.Lgs. 152/2006 as
 * amended by D.Lgs. 116/2020, with the operating detail in DM 360/2022.
 * Every separable packaging component must carry its material code, and the
 * consumer must be told which waste stream it belongs to.
 *
 * Codes follow Decisione 97/129/CE. This is the food-packaging subset, not the
 * full annex: the omitted families (textiles, most glass/metal composites) do
 * not occur on a food label produced from a Prospero recipe. Adding one is a
 * new entry here — deliberately not a DB CHECK constraint, so the catalogue
 * can grow without a migration.
 */
export interface PackagingMaterial {
  /** Printed verbatim on the label, e.g. `PP 5`. Persisted as the identity. */
  code: string;
  family: PackagingMaterialFamily;
  /** i18n key under `prepackagedLabel.packagingDisposal.materials`. */
  labelKey: string;
  /** Waste stream suggested when the operator picks this material. */
  defaultStream: PackagingDisposalStream;
}

export const PACKAGING_MATERIAL_FAMILIES = [
  'PLASTICA',
  'CARTA',
  'METALLO',
  'VETRO',
  'LEGNO',
  'ACCOPPIATI',
] as const;
export type PackagingMaterialFamily = (typeof PACKAGING_MATERIAL_FAMILIES)[number];

/**
 * Where the component goes. Italian kerbside collection is defined per comune,
 * so the label pairs the stream with the standard MASE disclaimer rather than
 * naming a specific municipal service.
 */
export const PACKAGING_DISPOSAL_STREAMS = [
  'PLASTICA',
  'CARTA',
  'VETRO',
  'METALLI',
  'ORGANICO',
  'INDIFFERENZIATO',
  'ISOLA_ECOLOGICA',
] as const;
export type PackagingDisposalStream = (typeof PACKAGING_DISPOSAL_STREAMS)[number];

export const PACKAGING_MATERIAL_CATALOG: readonly PackagingMaterial[] = [
  { code: 'PET 1', family: 'PLASTICA', labelKey: 'pet', defaultStream: 'PLASTICA' },
  { code: 'PE-HD 2', family: 'PLASTICA', labelKey: 'hdpe', defaultStream: 'PLASTICA' },
  { code: 'PVC 3', family: 'PLASTICA', labelKey: 'pvc', defaultStream: 'PLASTICA' },
  { code: 'PE-LD 4', family: 'PLASTICA', labelKey: 'ldpe', defaultStream: 'PLASTICA' },
  { code: 'PP 5', family: 'PLASTICA', labelKey: 'pp', defaultStream: 'PLASTICA' },
  { code: 'PS 6', family: 'PLASTICA', labelKey: 'ps', defaultStream: 'PLASTICA' },
  { code: 'O 7', family: 'PLASTICA', labelKey: 'otherPlastic', defaultStream: 'PLASTICA' },
  { code: 'PAP 20', family: 'CARTA', labelKey: 'corrugatedBoard', defaultStream: 'CARTA' },
  { code: 'PAP 21', family: 'CARTA', labelKey: 'board', defaultStream: 'CARTA' },
  { code: 'PAP 22', family: 'CARTA', labelKey: 'paper', defaultStream: 'CARTA' },
  { code: 'FE 40', family: 'METALLO', labelKey: 'steel', defaultStream: 'METALLI' },
  { code: 'ALU 41', family: 'METALLO', labelKey: 'aluminium', defaultStream: 'METALLI' },
  { code: 'GL 70', family: 'VETRO', labelKey: 'clearGlass', defaultStream: 'VETRO' },
  { code: 'GL 71', family: 'VETRO', labelKey: 'greenGlass', defaultStream: 'VETRO' },
  { code: 'GL 72', family: 'VETRO', labelKey: 'brownGlass', defaultStream: 'VETRO' },
  { code: 'FOR 50', family: 'LEGNO', labelKey: 'wood', defaultStream: 'INDIFFERENZIATO' },
  { code: 'FOR 51', family: 'LEGNO', labelKey: 'cork', defaultStream: 'INDIFFERENZIATO' },
  { code: 'C/PAP 81', family: 'ACCOPPIATI', labelKey: 'paperPlastic', defaultStream: 'CARTA' },
  { code: 'C/PAP 82', family: 'ACCOPPIATI', labelKey: 'paperAluminium', defaultStream: 'CARTA' },
  {
    code: 'C/PAP 84',
    family: 'ACCOPPIATI',
    labelKey: 'paperPlasticAluminium',
    defaultStream: 'CARTA',
  },
  {
    code: 'C/LDPE 90',
    family: 'ACCOPPIATI',
    labelKey: 'plasticAluminium',
    defaultStream: 'INDIFFERENZIATO',
  },
  {
    code: 'C/PP 92',
    family: 'ACCOPPIATI',
    labelKey: 'plasticMetal',
    defaultStream: 'INDIFFERENZIATO',
  },
] as const;

export const PACKAGING_MATERIAL_CODES: readonly string[] = PACKAGING_MATERIAL_CATALOG.map(
  (material) => material.code,
);

export function getPackagingMaterialByCode(code: string): PackagingMaterial | undefined {
  return PACKAGING_MATERIAL_CATALOG.find((material) => material.code === code);
}
